import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, User, MapPin, FileText, AlertTriangle, X, RefreshCw, ExternalLink } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';
import { groupNameClassifications, nameTypeClass } from '@/lib/nameTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
};

const labelOf = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.full_name || value.name || value.value || value.title || JSON.stringify(value);
};

const EntitySection = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</h3>
    {children}
  </div>
);

const EntityRecord = ({ entity }) => {
  const details = entity.details || {};
  // Each name keeps the type its source gave it — the backend classifies them.
  const nameGroups = groupNameClassifications(
    entity.name_classifications || details.name_classifications || []
  ).filter((group) => group.type !== 'Primary name');
  const programs = toArray(details.programs).concat(toArray(details.program_ids));
  const addresses = toArray(details.addresses);
  const nationalities = toArray(details.nationality).concat(toArray(details.nationalities));
  const ids = toArray(details.identification_documents);
  const sanctions = toArray(details.sanctions_details);

  return (
    <div className="space-y-5" data-testid="entity-details">
      <EntitySection title="Listed name">
        <div className="flex items-center">
          <User className="w-5 h-5 text-slate-400 mr-2 shrink-0" />
          <span className="text-lg font-semibold text-slate-900" data-testid="entity-primary-name">
            {entity.name || 'N/A'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="outline" className="text-[11px]">{entity.source}</Badge>
          {entity.entity_type && <Badge variant="outline" className="text-[11px]">{entity.entity_type}</Badge>}
          {entity.entity_id && (
            <Badge variant="outline" className="text-[11px] font-mono">{entity.entity_id}</Badge>
          )}
        </div>
      </EntitySection>

      {nameGroups.map(({ type, names }) => (
        <EntitySection key={`names-${type}`} title={`${type} (${names.length})`}>
          <div
            className="flex flex-wrap gap-1.5"
            data-testid={`lookup-name-group-${type.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {names.slice(0, 30).map((name, idx) => (
              <span
                key={`name-${type}-${name}-${idx}`}
                className={`px-2 py-0.5 rounded text-xs border ${nameTypeClass(type)}`}
              >
                {name}
              </span>
            ))}
            {names.length > 30 && (
              <span className="text-xs text-slate-400">+{names.length - 30} more</span>
            )}
          </div>
        </EntitySection>
      ))}

      {programs.length > 0 && (
        <EntitySection title="Sanctions programs">
          <div className="flex flex-wrap gap-2">
            {[...new Set(programs.map(labelOf))].map((program, idx) => (
              <span
                key={`prog-${program}-${idx}`}
                className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-xs font-medium"
              >
                {program}
              </span>
            ))}
          </div>
        </EntitySection>
      )}

      {sanctions.length > 0 && (
        <EntitySection title="Listing record">
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-1.5 text-slate-600">Program</th>
                  <th className="text-left px-2 py-1.5 text-slate-600">Resolution</th>
                  <th className="text-left px-2 py-1.5 text-slate-600">Listed</th>
                  <th className="text-left px-2 py-1.5 text-slate-600">Last amended</th>
                </tr>
              </thead>
              <tbody>
                {sanctions.map((sd, idx) => (
                  <tr key={`sd-${sd?.unsc_id || idx}`} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{sd?.program || '-'}</td>
                    <td className="px-2 py-1.5 font-mono">{sd?.un_resolution || sd?.program_id || '-'}</td>
                    <td className="px-2 py-1.5">{sd?.listing_date || sd?.start_date || '-'}</td>
                    <td className="px-2 py-1.5">{sd?.modified_date || details.modified_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EntitySection>
      )}

      {(details.birth_date || nationalities.length > 0 || details.gender) && (
        <EntitySection title="Identifiers">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {details.birth_date && (
              <div className="p-2 bg-slate-50 rounded">
                <span className="text-[10px] uppercase text-slate-500">Date of birth</span>
                <p className="text-sm font-medium text-slate-800">{labelOf(details.birth_date)}</p>
              </div>
            )}
            {nationalities.length > 0 && (
              <div className="p-2 bg-slate-50 rounded">
                <span className="text-[10px] uppercase text-slate-500">Nationality</span>
                <p className="text-sm font-medium text-slate-800">
                  {nationalities.map(labelOf).join(', ')}
                </p>
              </div>
            )}
            {details.gender && (
              <div className="p-2 bg-slate-50 rounded">
                <span className="text-[10px] uppercase text-slate-500">Gender</span>
                <p className="text-sm font-medium text-slate-800 capitalize">{labelOf(details.gender)}</p>
              </div>
            )}
            {ids.slice(0, 3).map((doc, idx) => (
              <div key={`id-${labelOf(doc)}-${idx}`} className="p-2 bg-slate-50 rounded">
                <span className="text-[10px] uppercase text-slate-500">Identification</span>
                <p className="text-sm font-medium text-slate-800">{labelOf(doc)}</p>
              </div>
            ))}
          </div>
        </EntitySection>
      )}

      {addresses.length > 0 && (
        <EntitySection title="Known locations">
          <div className="space-y-1">
            {addresses.slice(0, 15).map((addr, idx) => (
              <div key={`addr-${labelOf(addr)}-${idx}`} className="flex items-center text-sm text-slate-700">
                <MapPin className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
                {labelOf(addr)}
              </div>
            ))}
          </div>
        </EntitySection>
      )}

      {toArray(details.notes).length > 0 && (
        <EntitySection title="Narrative summary">
          <p className="text-xs text-slate-600 leading-relaxed">{toArray(details.notes).map(labelOf).join(' ')}</p>
        </EntitySection>
      )}

      <div className="pt-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[11px] text-slate-500">
          List data as of {(entity.synced_at || '').slice(0, 10) || 'unknown'}
        </span>
        <a
          href="https://sanctionssearch.ofac.treas.gov/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-blue-600 hover:underline"
        >
          <ExternalLink className="w-4 h-4 mr-1" />
          Verify on the official OFAC search
        </a>
      </div>
    </div>
  );
};

export default function OFACEntityLookup({ onClose }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookupEntity = async () => {
    const query = term.trim();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters of a name');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setSelected(null);

    try {
      const response = await axios.get(`${API}/entity/lookup`, {
        params: { name: query, limit: 15 },
        headers: getAuthHeaders()
      });
      const found = response.data?.results || [];
      setResults(found);
      if (found.length === 1) setSelected(found[0]);
      if (found.length === 0) setError(`No listing found for "${query}".`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Lookup failed. Please try again.';
      setError(typeof message === 'string' ? message : 'Lookup failed. Please try again.');
      toast.error(typeof message === 'string' ? message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center">
            <Search className="w-5 h-5 text-slate-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">Sanctions Entity Lookup</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="entity-lookup-close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 border-b border-slate-200">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Search by name (e.g. Khatiba Jama`at, Aerocaribbean)"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && lookupEntity()}
              className="flex-1"
              data-testid="entity-name-input"
            />
            <Button
              onClick={lookupEntity}
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="lookup-entity-button"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Lookup
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Searches listed names and all source-classified alternative names across OFAC, UNSCR, EU and MY MOHA records held in this system.
          </p>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <ErrorBoundary title="Entity record could not be displayed">
            {error && (
              <div
                className="flex items-center p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm"
                data-testid="entity-lookup-error"
              >
                <AlertTriangle className="w-5 h-5 mr-2 shrink-0" />
                {error}
              </div>
            )}

            {results.length > 1 && (
              <div className="mb-4 space-y-1" data-testid="entity-lookup-results">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  {results.length} records — select one
                </p>
                {results.map((item, idx) => (
                  <button
                    type="button"
                    key={`${item.entity_id || item.name}-${idx}`}
                    onClick={() => setSelected(item)}
                    data-testid={`entity-lookup-result-${idx}`}
                    className={`w-full text-left px-3 py-2 rounded border text-sm transition-colors ${
                      selected === item
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <span className="font-medium text-slate-900">{item.name}</span>
                    <span className="ml-2 text-xs text-slate-500">{item.source}</span>
                  </button>
                ))}
              </div>
            )}

            {selected && <EntityRecord entity={selected} />}

            {!selected && results.length === 0 && !error && !loading && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Search a name to view the official listing record</p>
                <p className="text-xs mt-2">Partial names and aliases are accepted</p>
              </div>
            )}
          </ErrorBoundary>
        </div>
      </Card>
    </div>
  );
}
